import React from 'react';
import moment from 'moment';
import { Link, useHistory } from 'react-router-dom';
import { useTable, useResizeColumns, usePagination, useFilters, useSortBy } from 'react-table';

import { getColor } from '../../utils';
import styles from './PowerfulTable.module.css';
import { download, get, put, patch } from '../../services/api';
import MdEye from 'react-ionicons/lib/MdEye';

const NotesCell = ({ initialValue, targetType, targetId }) => {
  const [value, setValue] = React.useState(initialValue || '');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  const onBlur = async () => {
    if (value !== (initialValue || '')) {
      setSaving(true);
      try {
        await put('/admin/notes', { targetType, targetId: String(targetId), note: value });
      } catch (err) {
        console.error('Failed to save note', err);
      }
      setSaving(false);
    }
  };

  return (
    <textarea
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={onBlur}
      style={{
        width: '100%',
        minHeight: '40px',
        border: saving ? '1px solid blue' : '1px solid #ccc',
        resize: 'vertical',
        fontSize: '0.85em',
        padding: '4px'
      }}
      placeholder="Ajouter une note..."
    />
  );
};

const ContactedCell = ({ initialValue, targetId, redirect }) => {
  const [value, setValue] = React.useState(initialValue || false);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setValue(initialValue || false);
  }, [initialValue]);

  const onChange = async (e) => {
    if (e) e.stopPropagation();
    const newValue = e.target.checked;
    setValue(newValue);
    setSaving(true);
    try {
      await patch(`/admin/${redirect}/${targetId}`, { contacted: newValue });
    } catch (err) {
      console.error('Failed to save contacted state', err);
      setValue(!newValue); // rollback
    }
    setSaving(false);
  };

  return (
    <input
      type="checkbox"
      checked={value}
      onChange={onChange}
      onClick={e => { if (e) e.stopPropagation(); }}
      disabled={saving}
      style={{
        cursor: saving ? 'wait' : 'pointer',
        width: '18px',
        height: '18px',
      }}
    />
  );
};

const dateRegex = new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);

// Define a default UI for filtering
const DefaultColumnFilter = ({
  column: { filterValue, setFilter },
}) => {
  return (
    <input
      value={filterValue || ''}
      onClick={e => e.stopPropagation()}
      onChange={e => {
        const val = e.target.value;
        setFilter(val || undefined) // Set undefined to remove the filter entirely
      }}
      placeholder={`...`}
      size={1}
      style={{
        width: '100%',
        margin: '0 auto',
        border: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.2)',
        backgroundColor: 'transparent',
        textAlign: 'center',
        outline: 'none',
        padding: '4px 0',
        fontSize: '0.85em',
        color: 'inherit',
        borderRadius: 0,
      }}
    />
  )
}

const LEVELS_ORDER = ['CP', 'CE1', 'CE2', 'CM1', 'CM2', 'Sixième', 'Cinquième', 'Quatrième', 'Troisième', 'Seconde', 'Première', 'Terminale', 'Supérieur'];

// Logic to extract sortable/filterable raw value
const getRawValue = (d, key) => {
  let val = d[key];
  if (typeof val === 'object' && val !== null) {
    val = val.name || val.status || '';
  }

  // Handle booleans explicitly for sorting
  if (typeof val === 'boolean') {
    return val ? 1 : 0;
  }

  // Handle null/undefined
  if (val === null || val === undefined) {
    return -1;
  }

  // Sort dates by timestamp
  if (typeof val === 'string' && dateRegex.test(val)) {
    return new Date(val).getTime();
  }

  // Sort levels by custom order
  // 'level' key might return a string like 'Sixième'.
  if (key === 'level') {
    const idx = LEVELS_ORDER.indexOf(val);
    return idx !== -1 ? idx : 99;
  }

  return val;
}

const PowerfulTable = (props) => {
  const history = useHistory();
  const [showColumnMenu, setShowColumnMenu] = React.useState(false);
  const data = React.useMemo(() => props.data, [props.data]);

  const [notesMap, setNotesMap] = React.useState({});
  
  React.useEffect(() => {
    if (props.redirect) {
      get(`/admin/notes?targetType=${props.redirect}`).then(res => {
        if (res && Array.isArray(res)) {
          const map = {};
          res.forEach(n => map[n.targetId] = n.note);
          setNotesMap(map);
        }
      }).catch(err => console.error(err));
    }
  }, [props.redirect]);

  const savedFilters = React.useMemo(() => {
    try {
      if (props.redirect) {
        const saved = localStorage.getItem(`filters_${props.redirect}`);
        if (saved) {
          return JSON.parse(saved);
        }
      }
    } catch (e) {
      console.error('Failed to parse saved filters', e);
    }
    return [];
  }, [props.redirect]);

  const columns = React.useMemo(
    () => ([
      ...Object.keys(props.types).map(key => ({
        Header: props.types[key].name,
        // accessor needs to return the sortable raw value
        accessor: (row) => getRawValue(row, key),
        id: key, // required when accessor is a function
        Cell: ((cell) => {
          // Format the raw value for display
          let value = cell.row.original[key];

          // Handle object extraction (same logic as getRawValue start) for display value
          if (typeof value === 'object' && value !== null) {
            let status = value.name || value.status || 'Actif';
            if (value.cancelled) {
              status = 'Annulé';
            }
            const provider = value.stripeSubscriptionId ? 'Stripe' : 'Axepta';
            value = `${provider} - ${status}`;
          }

          let showedValue = value;

          // Apply date formatting if needed for display
          // Logic for date detection might need to check if it's a date string (from row.original)
          if (typeof (value) === 'string' && dateRegex.test(value)) {
            showedValue = moment(value).format('DD-MM-YYYY à HH[h]mm');
          } else if (typeof (value) === 'boolean') {
            showedValue = value ? '✅' : '❌';
          }

          let style = {
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          };
          let textStyle = {
            fontSize: '0.9em',
            margin: 0,
            marginBlockStart: 0,
            marginBlockEnd: 0,
            lineHeight: '30px',
          }
          let onClick = (e) => null;
          let isHighValue = false;

          // Note: All highValue onClick functions should call e?.stopPropagation()
          // to prevent the tr (row) click handler from firing when clicking an interactive cell

          if (cell.column.id === '_id' || cell.column.id === 'id' || cell.column.id === 'courseId' || cell.column.id === 'subscriptionId' || cell.column.id === 'paymentId' || cell.column.id === 'newsId') {
            isHighValue = true;
            onClick = (e) => {
               if (e) e.stopPropagation();
               navigator.clipboard.writeText(cell.value);
            }
            style.cursor = 'pointer';
            textStyle.fontWeight = 'bold';
            if (cell.value && String(cell.value).length === 32) showedValue = 'Copy ID';
          }

          if (cell.column.id === 'amount' && parseInt(value, 10)) {
            showedValue = (parseInt(value, 10) / 100).toFixed(2)
          } else if (cell.column.id === 'rate' && value) {
            showedValue = parseFloat(value).toFixed(1)
          } else if (cell.column.id === 'status' && value) {
            isHighValue = true;
            onClick = (e) => {
               if (e) e.stopPropagation();
               cell.column.setFilter(value); // Set filter exactly as requested by user
            }
            style.cursor = 'pointer';

            const statusColors = {
              'ORDERED': '#805ad5',
              'CONFIRMED': '#3182ce',
              'DONE': '#38a169',
              'PUPIL_CANCELLED': '#e53e3e',
              'TUTOR_CANCELLED': '#e53e3e',
              'COMPLETED': '#38a169',
              'WAITING_TUTOR': '#dd6b20'
            };
            const color = statusColors[value] || '#718096';
            showedValue = (
              <span style={{ backgroundColor: color, color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '0.85em', fontWeight: '500' }}>
                {value}
              </span>
            );
          } else if (value === null || value === undefined) {
            showedValue = '-';
          }

          if (cell.column.id === 'contacted') {
            isHighValue = true;
            onClick = (e) => { if (e) e.stopPropagation(); };
            showedValue = (
               <ContactedCell 
                 initialValue={value} 
                 targetId={cell.row.original[props.redirectKey]} 
                 redirect={props.redirect} 
               />
            );
          }

          // Use the formatted showedValue or raw value for color check? Usually raw or formatted string.
          // getColor expects string/value.
          if (getColor(showedValue) !== 'black') {
            style.color = getColor(showedValue);
          }

          if (['teacher', 'pupil', 'createdBy', 'user'].includes(cell.column.id)) {
            isHighValue = true;
            style.cursor = 'pointer';
            textStyle.fontWeight = 'bold';
            if (cell.column.id === 'teacher') {
              onClick = (e) => { if (e) e.stopPropagation(); history.push('/teachers/' + value); };
            } else {
              onClick = (e) => { if (e) e.stopPropagation(); history.push('/pupils/' + value); };
            }
          } else if (cell.column.id === 'bill') {
            isHighValue = true;
            style.cursor = 'pointer';
            textStyle.textDecoration = 'underline';
            textStyle.color = 'blue';
            textStyle.fontSize = 14;
            onClick = (e) => {
              if (e) e.stopPropagation();
              download(value);
            }
            showedValue = 'Télécharger';
          } else if (cell.column.id === 'stripeSubscriptionId') {
            isHighValue = true;
            if (value) {
              showedValue = <a href={`https://dashboard.stripe.com/subscriptions/${value}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#635bff', textDecoration: 'none', fontWeight: 'bold' }}>Stripe Sub</a>;
            }
          } else if (cell.column.id === 'stripeCustomerId') {
            isHighValue = true;
            if (value) {
              showedValue = <a href={`https://dashboard.stripe.com/customers/${value}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#635bff', textDecoration: 'none', fontWeight: 'bold' }}>Stripe Cli</a>;
            }
          } else if (cell.column.id === 'stripePaymentId' || cell.column.id === 'transactionId') {
            isHighValue = true;
            // If it looks like a Stripe ID (pi_... or ch_... or even just exists)
            if (value && (String(value).startsWith('pi_') || String(value).startsWith('ch_'))) {
              showedValue = <a href={`https://dashboard.stripe.com/payments/${value}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: '#635bff', textDecoration: 'none', fontWeight: 'bold' }}>{value}</a>;
            }
          }

          // Return the highValue flag implicitly in the cell object so the table renderer can fetch it
          return <div style={style} onClick={onClick} className={isHighValue ? "marker-highvalue" : ""}><p style={textStyle}>{showedValue}</p></div>;
        }),
      })),
      {
        Header: 'Détails',
        accessor: 'link', // This might be undefined in raw data, handled safely?
        Cell: ({ row }) => {
          const id = row.original[props.redirectKey];
          return (<Link to={{ pathname: `/${props.redirect}/${id}` }} onClick={(e) => e.stopPropagation()}>Voir</Link>)
        },
        disableFilters: true, // Disable filter for this column
        disableSortBy: true,
      },
      {
        Header: 'Database',
        accessor: 'db-link',
        Cell: ({ row }) => {
          const id = row.original[props.redirectKey];
          return (<a target={"_blank"} rel="noopener noreferrer" href={`https://db.expliquemoipythagore.fr/_utils/#database/pythagore-shared/${id}`} onClick={(e) => e.stopPropagation()}>DB</a>)
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: 'Notes',
        accessor: 'notes_col',
        Cell: ({ row }) => {
          const id = row.original[props.redirectKey];
          if (!id || !props.redirect) return '-';
          return (
             <div onClick={(e) => e.stopPropagation()} className="marker-highvalue">
               <NotesCell initialValue={notesMap[String(id)]} targetType={props.redirect} targetId={id} />
             </div>
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
    ]),
    [props.redirect, props.redirectKey, props.types, notesMap],
  );

  const filterTypes = React.useMemo(
    () => ({
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          // id is actually an array of column IDs in v7, e.g. ['firstname']
          const colId = Array.isArray(id) ? id[0] : id;
          const rowValue = row.values[colId];
          if (rowValue === undefined || rowValue === null) return false;
          return String(rowValue)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        });
      },
    }),
    []
  );

  const savedHiddenColumns = React.useMemo(() => {
    try {
      if (props.redirect) {
        const saved = localStorage.getItem(`hidden_${props.redirect}`);
        if (saved) {
          return JSON.parse(saved);
        }
      }
    } catch (e) {
      console.error('Failed to parse saved hidden columns', e);
    }
    return [];
  }, [props.redirect]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    // rows, // now used for pagination implicitly or explicitly
    prepareRow,
    pageOptions,
    page,
    state: { pageIndex, pageSize, filters, hiddenColumns },
    gotoPage,
    setPageSize,
    allColumns, // Get allColumns for visibility toggle
    getToggleHideAllColumnsProps,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 200, filters: savedFilters, hiddenColumns: savedHiddenColumns },
      defaultColumn: { Filter: DefaultColumnFilter },
      filterTypes,
      style: { height: '400px' },
      autoResetFilters: false,
      autoResetSortBy: false,
      autoResetHiddenColumns: false,
    },
    useFilters,
    useSortBy,
    useResizeColumns,
    usePagination
  )

  React.useEffect(() => {
    if (props.redirect && filters) {
      localStorage.setItem(`filters_${props.redirect}`, JSON.stringify(filters));
    }
  }, [filters, props.redirect]);

  React.useEffect(() => {
    if (props.redirect && hiddenColumns) {
      localStorage.setItem(`hidden_${props.redirect}`, JSON.stringify(hiddenColumns));
    }
  }, [hiddenColumns, props.redirect]);

  return (
    <div className={styles.container}>
      <div style={{ padding: '10px 0', position: 'relative' }}>
        <button onClick={() => setShowColumnMenu(!showColumnMenu)} className={styles.button}>
          Colonnes ▾
        </button>
        {showColumnMenu && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            padding: '10px',
            zIndex: 10,
            minWidth: '150px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
          }}>
            <div style={{ marginBottom: 5 }}>
              <label>
                <input type="checkbox" {...getToggleHideAllColumnsProps()} /> Tout
              </label>
            </div>
            {allColumns.map(column => (
              <div key={column.id}>
                <label>
                  <input type="checkbox" {...column.getToggleHiddenProps()} />{' '}
                  {typeof column.Header === 'string' ? column.Header : column.id}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
      <table {...getTableProps()} className={styles.table}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => {
                const headerProps = column.getHeaderProps();
                return (
                <th
                  {...headerProps}
                  className={`${styles.cell} ${styles.capitalize}`}
                  style={{ position: 'relative', width: column.width, ...headerProps.style }}
                >
                  <div 
                    {...column.getSortByToggleProps()}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div 
                      className={styles.hideButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        column.toggleHidden(true);
                      }}
                      title="Masquer la colonne"
                    >
                      <MdEye fontSize="18px" color="#718096" />
                    </div>
                    {column.render('Header')}
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 5 }}>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (column.isSorted && !column.isSortedDesc) {
                            column.clearSortBy();
                          } else {
                            column.toggleSortBy(false); // false = ascending
                          }
                        }}
                        style={{ fontSize: '10px', color: column.isSorted && !column.isSortedDesc ? 'black' : '#ccc', cursor: 'pointer' }}
                      >▲</span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (column.isSorted && column.isSortedDesc) {
                            column.clearSortBy();
                          } else {
                            column.toggleSortBy(true); // true = descending
                          }
                        }}
                        style={{ fontSize: '10px', color: column.isSorted && column.isSortedDesc ? 'black' : '#ccc', cursor: 'pointer' }}
                      >▼</span>
                    </div>
                  </div>

                  <div style={{ fontWeight: 'normal' }}>
                    {column.canFilter ? column.render('Filter') : null}
                  </div>
                  <div
                    {...column.getResizerProps()}
                    className={`${styles.resizer} ${column.isResizing && styles.isResizing}`}
                    onClick={e => e.stopPropagation()} // Prevent sorting when resizing
                  />
                </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row)
            const rowId = row.original[props.redirectKey];
            const hasRedirect = !!(props.redirect && rowId);

            return (
              <tr 
                 {...row.getRowProps()}
                 className={hasRedirect ? styles.clickableRow : ""}
                 onClick={() => {
                   if (hasRedirect) {
                     history.push(`/${props.redirect}/${rowId}`);
                   }
                 }}
              >
                {row.cells.map(cell => {
                  // Retrieve the marker injected via className map logic
                  const cellNode = cell.render('Cell');
                  const isHighVal = cellNode && cellNode.props && cellNode.props.children && cellNode.props.children.props && cellNode.props.children.props.className && String(cellNode.props.children.props.className).includes('marker-highvalue');

                  return (
                    <td
                      {...cell.getCellProps([
                        { className: `${styles.cell} ${isHighVal ? styles.highValueCell : ""}` }
                      ])}
                      style={{ width: cell.column.width }}
                    >
                      {cellNode}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className={styles.pagination}>
        <div className={styles.pageSelect}>
          <select style={{ marginRight: 10 }} value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
          <p>lignes / page</p>
        </div>

        <div className={styles.pageSelect}>
          <p>Page</p>
          <select style={{ marginLeft: 10, marginRight: 10 }} value={pageIndex} onChange={(e) => gotoPage(e.target.value)}>
            {[...Array(pageOptions.length).keys()].map(i => (
              <option key={i} value={i}>{i + 1}</option>
            ))}
          </select>
          <p>/ {pageOptions.length}</p>
        </div>
      </div>
    </div>
  );
};

export default PowerfulTable;
